import React, { useCallback, useEffect, useRef, useState } from "react";

import { FormProvider, useWatch } from "react-hook-form";
import { useHistory } from "react-router-dom";

import useDeleteHousehold from "@fartherfinance/frontend/api/Households/hooks/useDeleteHousehold";
import { ClientId } from "@fartherfinance/frontend/api/Types";

import { useHouseholdContext } from "../../context";
import {
  HouseholdEstablishedAtFormField,
  HouseholdNameFormField,
} from "../../HouseholdFormFields";
import ButtonPrimary from "@src/multiCustodian/components/MUI/Button/Button";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import FormBody from "@src/sharedComponents/Forms/FormBody";
import DeleteModal from "@src/sharedComponents/Modal/DeleteModal/DeleteModal";
import Text from "@src/sharedComponents/Typography/Text";
import { handleApiError } from "@src/utils/handleApiError";

import AddHouseholdMember from "./AddHouseholdMember";
import { HouseholdMemberRelationshipFormField } from "./HouseholdMemberRelationshipFormField";

import styles from "./HouseholdProfile.module.css";

function DeleteHouseholdButton() {
  const { householdId } = useHouseholdContext();

  const auth = useAdvisorRequestAuth();
  const notify = useStatusNotification();

  const { mutateAsync: deleteHousehold } = useDeleteHousehold(auth);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const history = useHistory();

  const closeDrawer = useCallback(
    () =>
      history.push({ pathname: "/Advisor/Clients", search: location.search }),
    [history]
  );

  const handleDeleteHousehold = async () => {
    try {
      await deleteHousehold({ householdId });
      setShowDeleteModal(false);
      closeDrawer();
    } catch (error) {
      handleApiError({
        error: error,
        errorMessage: "Error deleting household member:",
        genericErrorMessage: "Error deleting household member",
        notify,
      });
    }
  };

  const handleShowDeleteModal = () => {
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };

  return (
    <>
      <ButtonPrimary
        text="Delete Household"
        onClick={handleShowDeleteModal}
        variant="outlined"
        buttonType="primary"
        className={styles.rightAlignedBtn}
        sx={{ height: "32px" }}
      />
      <DeleteModal
        isOpen={showDeleteModal}
        title="Delete Household"
        text="Deleting this household will remove it and all associated members. This action cannot be undone."
        deleteButtonText="Delete Household"
        onDelete={handleDeleteHousehold}
        onClose={handleCloseDeleteModal}
      />
    </>
  );
}

export default function HouseholdProfile() {
  const { form, householdId } = useHouseholdContext();
  const { control } = form;

  const householdOwners = useWatch({
    control,
    name: "owners",
  });

  const householdMembers = useWatch({
    control,
    name: "members",
  });

  const [isOverflowing, setIsOverflowing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkOverflow = () => {
      if (containerRef.current) {
        setIsOverflowing(
          containerRef.current.scrollHeight > containerRef.current.clientHeight
        );
      }
    };

    checkOverflow();

    window.addEventListener("resize", checkOverflow);

    return () => {
      window.removeEventListener("resize", checkOverflow);
    };
  }, [householdOwners, householdMembers]);

  return (
    <div className={styles.profileContainer} ref={containerRef}>
      <DeleteHouseholdButton />
      <FormProvider {...form}>
        <form>
          <FormBody className={styles.sectionSpacing}>
            <HouseholdNameFormField label="Household" />
            <HouseholdEstablishedAtFormField label="Established On (Optional)" />
          </FormBody>
          <Text variant="title3" className={styles.sectionSpacing}>
            Household Members
          </Text>
          {Object.keys(householdOwners).map((clientId) => (
            <HouseholdMemberRelationshipFormField
              memberName={`owners.${clientId}.memberOption`}
              relationshipName={`owners.${clientId}.relationshipOption`}
              key={clientId}
              className={styles.memberFormField}
              withDeleteButton
              clientId={clientId as ClientId}
              householdId={householdId}
            />
          ))}
          {Object.keys(householdMembers).map((clientId) => (
            <HouseholdMemberRelationshipFormField
              memberName={`members.${clientId}.memberOption`}
              relationshipName={`members.${clientId}.relationshipOption`}
              key={clientId}
              className={styles.memberFormField}
              withDeleteButton
              clientId={clientId as ClientId}
              householdId={householdId}
            />
          ))}
          <div className={isOverflowing ? styles.stickyBottom : undefined}>
            <AddHouseholdMember />
          </div>
        </form>
      </FormProvider>
    </div>
  );
}
